// @givmo/board — the fields every compensation decision is entered with (CBG-YR4-PAY, frame c1): whose
// pay it sets, in the organization's own words, and the organization's own Form 990 answer for that
// person. One component for the motion's dialog, a consent's, an index entry's and the label's own,
// so the four never say it two ways.
//
// THE 990 ANSWER IS A LABEL THE ORGANIZATION GIVES, NEVER ONE BOARD DECIDES. It is asked only where
// the host's organizations answer Form 990 (`useBoardAnswersForm990`; the backend takes a line for the
// one class that files it, the contract's §10), with neither line chosen until the officer chooses, and the
// hint says whose answer it is.

import { RadioTile, RadioTileGroup, TextField } from '@givmo/design-system';
import { PartLabel } from '../conflicts/coi-parts';
import type { Form990Line } from './compensation-decode';
import { compensationWords as w } from './compensation-words';

const SMALL = 'font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary';

/** The third choice's value: the organization's answer taken back to none. */
const NEITHER = 'neither';

/**
 * The organization's Form 990 answer for this person: line 15a or 15b, or none chosen yet. Where
 * `allowNeither` (the list's change dialog only), a third choice takes a chosen answer back to none.
 */
export function Form990Answer({
  value,
  onChange,
  testId,
  showLabel = true,
  allowNeither = false,
}: {
  value: Form990Line | null;
  onChange: (line: Form990Line | null) => void;
  testId: string;
  /** False where the dialog's own title already says it ("Your Form 990 answer"). */
  showLabel?: boolean;
  allowNeither?: boolean;
}) {
  return (
    <div className="flex flex-col gap-6">
      {showLabel ? (
        <span className="font-sans text-s12 font-w600 tracking-givmo text-text-strong">
          {w.lineLabel}
        </span>
      ) : null}
      <RadioTileGroup
        value={value ?? (allowNeither ? NEITHER : '')}
        onValueChange={(next) => {
          if (next === '15a' || next === '15b') onChange(next);
          else if (allowNeither && next === NEITHER) onChange(null);
        }}
        aria-label={w.lineLabel}
        className="gap-6"
        testId={testId}
      >
        <RadioTile value="15a" label={w.line15a} testId={`${testId}-15a`} />
        <RadioTile value="15b" label={w.line15b} testId={`${testId}-15b`} />
        {allowNeither ? (
          <RadioTile value={NEITHER} label={w.lineNeither} testId={`${testId}-neither`} />
        ) : null}
      </RadioTileGroup>
      <span className={SMALL}>{w.lineHint}</span>
    </div>
  );
}

/** Whose pay this sets: the name and the position, each one line, and the Form 990 answer. */
export function WhosePayFields({
  name,
  position,
  line,
  answersForm990,
  onName,
  onPosition,
  onLine,
  testId,
}: {
  name: string;
  position: string;
  line: Form990Line | null;
  answersForm990: boolean;
  onName: (value: string) => void;
  onPosition: (value: string) => void;
  onLine: (line: Form990Line | null) => void;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-10">
      <PartLabel>{w.whosePay}</PartLabel>
      <div className="grid grid-cols-1 gap-12 sm:grid-cols-2">
        <TextField
          label={w.nameLabel}
          value={name}
          maxLength={200}
          onChange={(event) => onName(event.target.value)}
          testId={`${testId}-name`}
        />
        <TextField
          label={w.positionLabel}
          value={position}
          maxLength={200}
          onChange={(event) => onPosition(event.target.value)}
          testId={`${testId}-position`}
        />
      </div>
      {answersForm990 ? (
        <Form990Answer value={line} onChange={onLine} testId={`${testId}-line`} />
      ) : null}
    </div>
  );
}
